import { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import {
  truckForSaleFieldConfig,
  truckForSaleInitialValues,
} from "../../../formConfigs/Sale/truckForSaleFieldConfig";

interface UpdateTruckForSalePopupProps {
  truckForSale: any;
}

const UpdateTruckForSalePopup = ({
  truckForSale,
}: UpdateTruckForSalePopupProps) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(truckForSaleInitialValues);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { token } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open && truckForSale) {
      setInitialValues({
        unitNumber: truckForSale.unitNumber ?? "",
        make: truckForSale.make ?? "",
        model: truckForSale.model ?? "",
        year: truckForSale.year ?? "",
        VIN: truckForSale.vin ?? "",
        engineType: truckForSale.engineType ?? "",
        location: truckForSale.location ?? "",
        transmission: truckForSale.transmission ?? "",
        suspension: truckForSale.suspension ?? "",
        drive: truckForSale.drive ?? "",
        axleRatio: truckForSale.axleRatio ?? "",
        wheelbase: truckForSale.wheelbase ?? "",
        brakes: truckForSale.brakes ?? "",
        sleeper: truckForSale.sleeper ?? "",
        mileage: truckForSale.mileage ?? 0,
        price: truckForSale.price ?? 0,
        description: truckForSale.description ?? "",
        isTruckFeatured: truckForSale.isTruckFeatured ?? false,
        isActive: truckForSale.isActive ?? false,
      });
    }
  }, [open, truckForSale]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/truckSale`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["trucksForSale"] });
      setOpen(false);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(
        error.response?.data || "Failed to update truck for sale"
      );
    },
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const submitHandler = (values: any) => {
    const finalData = {
      ...values,
      id: truckForSale?.id,
    };
    updateMutation.mutate(finalData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update
      </Button>

      <GenericFormPopup
        formFields={truckForSaleFieldConfig}
        initialValues={initialValues}
        title="Update Truck For Sale"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={updateMutation.isPending}
      />

      <StatusSnackbar
        status={status}
        successMessage="Truck for sale updated successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateTruckForSalePopup;
